import { z } from 'zod';
import { randomBytes } from 'crypto';

/**
 * Pure logic + validation for `ss dev configure`.
 *
 * This module performs NO file I/O — it only validates a spec and builds the
 * config fragments (connection string, appsettings.Local.json patch, the
 * launchSettings `Local` profile, the frontend env content). The CLI wiring in
 * `commands/dev.ts` reads/writes the files and calls these builders.
 *
 * Goal: make the get-it-wrong-once-and-auth-breaks work (connection string,
 * JWT, `ASPNETCORE_ENVIRONMENT=Local`) deterministic and unit-testable.
 */

// ── Spec ─────────────────────────────────────────────────────────────────────

export const ConfigureSpecSchema = z
  .object({
    apiPort: z.number().int().min(1).max(65535),
    /**
     * DEPRECATED — dev is HTTP-only. Accepted for backward-compat with older
     * specs (and `--auto-port`) but IGNORED: no HTTPS URL is written into the
     * Local profile, so nothing 307-redirects to an untrusted dev cert.
     */
    httpsPort: z.number().int().min(1).max(65535).optional(),
    webPort: z.number().int().min(1).max(65535),
    /**
     * 'sql' → SQL Server auth (login+password) ; 'windows' → Integrated Security.
     * OPTIONAL: omit `authMode` + `sqlServer` + `sqlDatabase` together for a
     * **ports-only** reconfigure that PRESERVES the existing connection string +
     * JWT (only ports + CORS/FrontendUrl change). Supply all three to (re)write
     * the connection string.
     */
    authMode: z.enum(['sql', 'windows']).optional(),
    sqlServer: z.string().min(1).optional(),
    sqlDatabase: z.string().min(1).optional(),
    sqlLogin: z.string().optional(),
    sqlPassword: z.string().optional(),
    /**
     * A complete, pre-built DefaultConnection string to write VERBATIM. Mutually
     * exclusive with the SQL block (authMode/sqlServer/sqlDatabase). The orchestrator's
     * auto-seed path computes it (copy the base appsettings connection string, swap the
     * per-branch DB name) and passes it here so `configure` can write a FRESH
     * appsettings.Local.json with no SQL prompt. When present it wins over hasSqlSpec().
     */
    connectionString: z.string().min(1).optional(),
    /** Auto-generated when absent (and no non-empty secret already on disk). */
    jwtSecret: z.string().optional(),
    /**
     * Initial app admin (Security.InitialAdmin). MANDATORY for a bootable app: the
     * backend fail-fasts on first boot without it and the dev cartouche can't show a
     * login. Written into appsettings.Local.json (gitignored) — both are all-or-nothing
     * (see validateSpec). The dev seed proposes a default login + a generated strong
     * password; the user confirms/edits before the secret is persisted.
     */
    initialAdminEmail: z.string().optional(),
    initialAdminPassword: z.string().optional(),
    /**
     * When true, `ss dev configure` probes the SQL server and CREATEs the target
     * database if it does not exist yet (best-effort, via sqlcmd — see lib/sql-probe).
     * sqlcmd absent / server unreachable downgrades to a warning (EF AutoMigrate still
     * creates the schema on boot); it never blocks writing the config files.
     */
    ensureDatabase: z.boolean().optional(),
    apiDir: z.string().optional(),
    webDir: z.string().optional(),
    /**
     * Dev default for `FileStorage:BasePath`, written into appsettings.Local.json.
     * The platform's `LocalFileStorageService` fail-fasts when BasePath is empty
     * (the project template ships `""`), 400-ing the branding + tenant-preferences
     * endpoints at boot. `ss dev` computes an absolute `<apiDir>/app-files` and
     * passes it here ONLY when the existing Local config has no non-empty value, so
     * a user's custom path is preserved. Optional + gitignored → prod untouched.
     */
    fileStorageBasePath: z.string().optional(),
  })
  .strict();

export type ConfigureSpec = z.infer<typeof ConfigureSpecSchema>;

/**
 * True when the spec carries a COMPLETE SQL block (→ (re)build the connection
 * string). False = ports-only reconfigure → preserve the existing connection
 * string untouched.
 */
export function hasSqlSpec(spec: ConfigureSpec): boolean {
  return Boolean(spec.authMode && spec.sqlServer && spec.sqlDatabase);
}

/**
 * Cross-field validation that Zod cannot express ergonomically.
 * Returns a list of blocking messages (empty = valid).
 */
export function validateSpec(spec: ConfigureSpec): string[] {
  const blockers: string[] = [];

  // Dev is HTTP-only → only the API (http) and web ports are written, and they
  // must differ. httpsPort is accepted but ignored (legacy specs), so it is not
  // part of the distinctness rule.
  if (spec.apiPort === spec.webPort) {
    blockers.push(`API and web ports must differ (api=${spec.apiPort}, web=${spec.webPort}).`);
  }

  // SQL block is all-or-nothing: all three → (re)write the connection string;
  // none → ports-only reconfigure (preserve the existing connection string).
  const sqlGiven = [spec.authMode, spec.sqlServer, spec.sqlDatabase].filter(Boolean).length;
  if (sqlGiven > 0 && sqlGiven < 3) {
    blockers.push(
      'Provide authMode + sqlServer + sqlDatabase together, or none (ports-only — preserves the existing connection string).'
    );
  }

  // connectionString is an ALTERNATIVE to the SQL block (the auto-seed path builds it
  // directly), so the two are mutually exclusive — never two sources of truth.
  if (spec.connectionString && sqlGiven > 0) {
    blockers.push(
      'Provide either connectionString (pre-built) OR the SQL block (authMode + sqlServer + sqlDatabase), not both.'
    );
  }

  if (spec.authMode === 'sql') {
    if (!spec.sqlLogin || spec.sqlLogin.trim().length === 0) {
      blockers.push('sqlLogin is required when authMode is "sql".');
    }
    if (!spec.sqlPassword || spec.sqlPassword.length === 0) {
      blockers.push('sqlPassword is required when authMode is "sql".');
    }
  }

  // Initial admin is all-or-nothing, and both must be well-formed. These are the
  // mandatory app-login elements seeded into appsettings.Local.json — a half-set
  // (email without password) yields an app that fail-fasts on first boot.
  const adminGiven = [spec.initialAdminEmail, spec.initialAdminPassword].filter(
    (v) => v !== undefined && v.length > 0
  ).length;
  if (adminGiven === 1) {
    blockers.push('Provide initialAdminEmail + initialAdminPassword together (or neither).');
  }
  if (spec.initialAdminEmail && spec.initialAdminEmail.length > 0 && !validateAdminEmail(spec.initialAdminEmail)) {
    blockers.push(`initialAdminEmail "${spec.initialAdminEmail}" is not a valid email address.`);
  }
  if (spec.initialAdminPassword && spec.initialAdminPassword.length > 0) {
    blockers.push(...validateAdminPassword(spec.initialAdminPassword));
  }

  // When a SQL block is supplied, the server + database name must be sane — the
  // database name is embedded in `CREATE DATABASE [<name>]` by the ensure-DB step,
  // so a strict SQL-identifier check is also an injection guard.
  if (spec.sqlServer && spec.sqlServer.length > 0) {
    blockers.push(...validateSqlServer(spec.sqlServer));
  }
  if (spec.sqlDatabase && spec.sqlDatabase.length > 0) {
    blockers.push(...validateSqlDatabaseName(spec.sqlDatabase));
  }

  return blockers;
}

// ── Mandatory dev credentials (admin login + strong password) ───────────────────

/** Default dev admin login proposed when the project configures none. */
export const DEFAULT_ADMIN_EMAIL = 'local.admin@smartstack.local';

/** Unbiased integer in [0, max) from crypto bytes (rejection sampling, max ≤ 256). */
function randomIntBelow(max: number): number {
  if (max <= 1) return 0;
  const limit = Math.floor(256 / max) * max; // largest multiple of max ≤ 256
  let b = randomBytes(1)[0];
  while (b >= limit) b = randomBytes(1)[0];
  return b % max;
}

/**
 * Generate a strong dev password: `length` chars (default 20) with at least one
 * uppercase, lowercase, digit and special char, drawn unbiasedly from
 * crypto.randomBytes then shuffled (Fisher–Yates, crypto-random).
 *
 * The alphabets exclude ambiguous glyphs (0/O/1/l/I) and characters that would be
 * hostile inside a JSON config, a PowerShell/Bash `--spec`, or a copy-pasted login
 * (`" ' \` \\ $ space ;`). The result satisfies `validateAdminPassword`.
 */
export function generateStrongPassword(length = 20): string {
  const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const LOWER = 'abcdefghijkmnpqrstuvwxyz';
  const DIGIT = '23456789';
  const SPECIAL = '!@#%^*_-+=?.';
  const ALL = UPPER + LOWER + DIGIT + SPECIAL;
  const pick = (alphabet: string): string => alphabet[randomIntBelow(alphabet.length)];
  const n = Math.max(length, 4);
  // Guarantee one of each class, fill the rest from the full alphabet…
  const chars = [pick(UPPER), pick(LOWER), pick(DIGIT), pick(SPECIAL)];
  while (chars.length < n) chars.push(pick(ALL));
  // …then shuffle so the guaranteed chars aren't pinned to the first four slots.
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomIntBelow(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}

/** Basic email-shape check for the dev admin login. */
export function validateAdminEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email ?? '').trim());
}

/**
 * SmartStack dev admin password policy (mirrors ASP.NET Identity defaults plus
 * SmartStack hardening): ≥ 12 chars with an uppercase, a lowercase, a digit and a
 * non-alphanumeric. Returns blocking messages (empty = strong enough).
 */
export function validateAdminPassword(pwd: string): string[] {
  const out: string[] = [];
  const p = pwd ?? '';
  if (p.length < 12) out.push('Admin password must be at least 12 characters.');
  if (!/[A-Z]/.test(p)) out.push('Admin password must contain an uppercase letter.');
  if (!/[a-z]/.test(p)) out.push('Admin password must contain a lowercase letter.');
  if (!/[0-9]/.test(p)) out.push('Admin password must contain a digit.');
  if (!/[^A-Za-z0-9]/.test(p)) out.push('Admin password must contain a special character.');
  return out;
}

/** SQL Server system databases that must never be (re)created as the app DB. */
const RESERVED_DB_NAMES = new Set(['master', 'tempdb', 'model', 'msdb']);

/**
 * Validate a SQL Server database name: a regular identifier (letter/underscore
 * start, then letters/digits/underscore), ≤ 128 chars, not a system database.
 * Returns blocking messages (empty = valid). A passing name is safe to embed in
 * `CREATE DATABASE [<name>]` (no `]` can appear) — bracket-injection guard.
 */
export function validateSqlDatabaseName(name: string): string[] {
  const out: string[] = [];
  const n = (name ?? '').trim();
  if (n.length === 0) {
    out.push('Database name is required.');
    return out;
  }
  if (n.length > 128) out.push('Database name must be at most 128 characters.');
  if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(n)) {
    out.push(
      'Database name must be a SQL identifier: a letter or underscore, then letters, digits or underscores (no spaces or punctuation).'
    );
  }
  if (RESERVED_DB_NAMES.has(n.toLowerCase())) out.push(`"${n}" is a reserved system database name.`);
  return out;
}

/**
 * Validate a SQL Server host. Permissive — accepts `host`, `host\instance`,
 * `host,port`, `(local)`, `(localdb)\MSSQLLocalDB`, `tcp:host` — and rejects only
 * clearly broken values (whitespace, quotes, semicolons). The live probe in
 * lib/sql-probe is the real reachability check; this just catches typos early.
 */
export function validateSqlServer(server: string): string[] {
  const s = (server ?? '').trim();
  if (s.length === 0) return ['SQL Server is required.'];
  if (/[\s;'"]/.test(s)) {
    return [`SQL Server "${s}" contains an invalid character (whitespace, quote or semicolon).`];
  }
  return [];
}

type ParseResult = { ok: true; spec: ConfigureSpec } | { ok: false; errors: string[] };

/**
 * Validate an already-parsed spec object (shape via Zod + cross-field rules).
 */
export function validateConfigureSpecObject(raw: unknown): ParseResult {
  const parsed = ConfigureSpecSchema.safeParse(raw);
  if (!parsed.success) {
    return {
      ok: false,
      errors: parsed.error.errors.map((e) => `${e.path.join('.') || '(root)'}: ${e.message}`),
    };
  }
  const blockers = validateSpec(parsed.data);
  if (blockers.length > 0) return { ok: false, errors: blockers };
  return { ok: true, spec: parsed.data };
}

/**
 * Parse + validate a raw `--spec` JSON string.
 */
export function parseConfigureSpec(json: string): ParseResult {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch (e) {
    return { ok: false, errors: [`Invalid JSON spec: ${(e as Error).message}`] };
  }
  return validateConfigureSpecObject(raw);
}

// ── Builders ──────────────────────────────────────────────────────────────────

/** Pooling tail kept identical to templates/project/appsettings.json.template. */
const CONN_TAIL =
  'TrustServerCertificate=true;Connection Timeout=60;Pooling=true;Min Pool Size=0;Max Pool Size=50;Load Balance Timeout=30';

/**
 * Build the SQL Server `DefaultConnection` string.
 * - windows → Integrated Security (no login/password)
 * - sql     → User Id + Password
 */
export function buildSqlConnString(spec: ConfigureSpec): string {
  const { sqlServer, sqlDatabase, authMode } = spec;
  if (!sqlServer || !sqlDatabase || !authMode) {
    throw new Error('buildSqlConnString requires a complete SQL spec (authMode + sqlServer + sqlDatabase).');
  }
  const head = `Server=${sqlServer};Database=${sqlDatabase};`;
  const auth =
    authMode === 'windows'
      ? 'Integrated Security=true;'
      : `User Id=${spec.sqlLogin};Password=${spec.sqlPassword};`;
  return head + auth + CONN_TAIL;
}

/**
 * Decide the JWT secret: spec wins, then any non-empty secret already on disk,
 * else a freshly generated one. `generated` is true only in the last case
 * (so re-running configure does not churn an existing secret → idempotent).
 */
export function resolveJwtSecret(
  specSecret: string | undefined,
  existingSecret: string | null | undefined
): { secret: string; generated: boolean } {
  if (specSecret && specSecret.trim().length > 0) return { secret: specSecret, generated: false };
  if (existingSecret && existingSecret.trim().length > 0) {
    return { secret: existingSecret, generated: false };
  }
  return { secret: randomBytes(64).toString('base64'), generated: true };
}

/**
 * Build the patch to deep-merge into appsettings.Local.json.
 *
 * Writes the keys the SmartStack app actually reads at runtime —
 * `SmartStack.FrontendUrl` + `SmartStack.CorsOrigins` — so CORS stays coherent
 * when the frontend port changes (see appsettings.json.template).
 */
export function buildAppsettingsLocalPatch(
  spec: ConfigureSpec,
  existingSecret: string | null | undefined
): { patch: Record<string, unknown>; jwtGenerated: boolean } {
  const { secret, generated } = resolveJwtSecret(spec.jwtSecret, existingSecret);
  const webOrigin = `http://localhost:${spec.webPort}`;
  const patch: Record<string, unknown> = {
    Jwt: { Secret: secret },
    SmartStack: { FrontendUrl: webOrigin, CorsOrigins: [webOrigin] },
  };
  // A pre-built connectionString (auto-seed path) wins; else a complete SQL block
  // (re)builds it; else (ports-only) omit ConnectionStrings so the deep-merge preserves
  // the existing one.
  if (spec.connectionString) {
    patch.ConnectionStrings = { DefaultConnection: spec.connectionString };
  } else if (hasSqlSpec(spec)) {
    patch.ConnectionStrings = { DefaultConnection: buildSqlConnString(spec) };
  }
  // Mandatory app login: seed Security.InitialAdmin (login + password) into the
  // gitignored Local file. validateSpec guarantees both are present together and
  // well-formed, so a partial write is impossible. Without this the backend
  // fail-fasts on first boot and the cartouche can't show a login.
  if (spec.initialAdminEmail && spec.initialAdminPassword) {
    patch.Security = {
      InitialAdmin: { Email: spec.initialAdminEmail, Password: spec.initialAdminPassword },
    };
  }
  // Seed a dev FileStorage:BasePath when the caller resolved one (the existing Local
  // value was empty). The platform fail-fasts on an empty BasePath. dev.ts only sets
  // fileStorageBasePath when there is nothing to preserve, so a custom path survives.
  if (spec.fileStorageBasePath && spec.fileStorageBasePath.trim().length > 0) {
    patch.FileStorage = { BasePath: spec.fileStorageBasePath };
  }
  return { patch, jwtGenerated: generated };
}

/**
 * Build the launchSettings `Local` profile.
 *
 * CRITICAL: `ASPNETCORE_ENVIRONMENT=Local` is what makes the backend load
 * appsettings.Local.json (the connection string + JWT we just wrote). It is
 * also why `ss dev detect` prefers the `Local` profile + `--launch-profile Local`.
 *
 * HTTP-ONLY by design: the profile serves on `http://localhost:<api>` with NO
 * https URL. With no https port, ASP.NET's `UseHttpsRedirection` has nothing to
 * 307-redirect to, so it no-ops — the frontend and the `ss dev` health probe
 * both talk plain HTTP and never hit the untrusted dev cert that otherwise breaks
 * every browser call with ERR_CERT_AUTHORITY_INVALID. This single choice removes
 * the third port entirely (api + web only) and the whole 307 / cert class of bugs.
 */
export function buildLocalProfile(apiPort: number): Record<string, unknown> {
  return {
    commandName: 'Project',
    dotnetRunMessages: true,
    launchBrowser: false,
    applicationUrl: `http://localhost:${apiPort}`,
    environmentVariables: { ASPNETCORE_ENVIRONMENT: 'Local' },
  };
}

/**
 * Return a launchSettings object with its `Local` profile set/replaced, every
 * other profile (http/https/…) and top-level key ($schema, iisSettings) preserved.
 */
export function applyLocalProfile(
  existing: Record<string, unknown> | null,
  apiPort: number
): Record<string, unknown> {
  const base: Record<string, unknown> =
    existing && typeof existing === 'object' && !Array.isArray(existing) ? { ...existing } : {};
  const profiles: Record<string, unknown> =
    base.profiles && typeof base.profiles === 'object' && !Array.isArray(base.profiles)
      ? { ...(base.profiles as Record<string, unknown>) }
      : {};
  profiles.Local = buildLocalProfile(apiPort);
  base.profiles = profiles;
  return base;
}

/**
 * Upsert `VITE_API_URL` + `VITE_PORT` in a frontend env file's content.
 * Replaces matching lines in place (preserving comments + other vars) or
 * appends them. Seeds a header when the file is empty.
 */
export function patchEnvContent(existing: string, apiPort: number, webPort: number): string {
  let content = existing ?? '';
  if (content.trim().length === 0) {
    content = '# Generated by `ss dev configure` — local dev overrides (gitignored)\n';
  }
  content = upsertEnvLine(content, 'VITE_API_URL', `http://localhost:${apiPort}`);
  content = upsertEnvLine(content, 'VITE_PORT', String(webPort));
  return content;
}

function upsertEnvLine(content: string, key: string, value: string): string {
  const re = new RegExp(`^${key}\\s*=.*$`, 'm');
  const line = `${key}=${value}`;
  if (re.test(content)) return content.replace(re, line);
  const sep = content.length === 0 || content.endsWith('\n') ? '' : '\n';
  return content + sep + line + '\n';
}

/**
 * Ensure each pattern is present as its own line in a .gitignore's content.
 * Returns the (possibly unchanged) content + the patterns that were added.
 * Idempotent: patterns already present (ignoring comments/blank lines) are left
 * untouched. Used to keep appsettings.Local.json (SQL password + JWT) out of git.
 */
export function ensureGitignoreLines(
  existing: string,
  patterns: string[]
): { content: string; added: string[] } {
  const base = existing ?? '';
  const present = new Set(
    base
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0 && !l.startsWith('#'))
  );
  const added = patterns.filter((p) => !present.has(p.trim()));
  if (added.length === 0) return { content: base, added: [] };
  let content = base;
  if (content.length > 0 && !content.endsWith('\n')) content += '\n';
  content += '\n# SmartStack dev — local config with secrets (never commit)\n' + added.join('\n') + '\n';
  return { content, added };
}

/**
 * Extract the API (plain-HTTP) port a launchSettings `applicationUrl` serves on.
 *
 * `buildLocalProfile` writes the dual url https-FIRST
 * (`https://localhost:7142;http://localhost:5142`), but the port the frontend
 * talks to (VITE_API_URL) and the one `ss dev status`/`summary` probe is the
 * plain-HTTP one (5142). A naive "first :port" match returns the HTTPS port
 * (7142) → a phantom mode-mismatch in `ss dev detect` → the reconfigure spiral.
 * So prefer the `http://` port; fall back to the first port only for an
 * https-only profile (which legitimately serves on https).
 */
export function extractApiPort(applicationUrl: string | null | undefined): number | null {
  if (!applicationUrl) return null;
  // `http://` cannot match inside `https://` (the char after `http` is `s`, not `:`),
  // so this skips the https segment and lands on the plain-http one wherever it sits.
  const http = applicationUrl.match(/http:\/\/[^;,\s]*:(\d{4,5})/);
  if (http) return parseInt(http[1], 10);
  const first = applicationUrl.match(/:(\d{4,5})/);
  return first ? parseInt(first[1], 10) : null;
}

/**
 * True when a launchSettings `applicationUrl` still serves HTTPS (a legacy dual
 * `https://…;http://…` profile). Dev is HTTP-only now, so `ss dev detect` flags
 * such a profile for one-time normalization — otherwise an EXISTING project keeps
 * its https url, `UseHttpsRedirection` 307-redirects the plain-http calls, and the
 * untrusted dev cert breaks every browser call (the exact PickEBike state). New
 * projects + every `ss dev configure` already write the http-only profile, so this
 * only ever catches pre-existing launchSettings.
 */
export function profileServesHttps(applicationUrl: string | null | undefined): boolean {
  return /https:\/\//i.test(applicationUrl ?? '');
}

/**
 * Pick a base port to scan for a free HTTPS port: the existing 2nd port of the
 * launchSettings applicationUrl (the https one), else `apiPort + 2000` (≈7142,
 * matching the init.ts http=5142/https=7142 convention).
 */
export function deriveHttpsBase(applicationUrl: string | null | undefined, apiPort: number): number {
  if (applicationUrl) {
    const httpsMatch = applicationUrl.match(/https:\/\/[^;]*:(\d{4,5})/);
    if (httpsMatch) return parseInt(httpsMatch[1], 10);
    const ports = [...applicationUrl.matchAll(/:(\d{4,5})/g)].map((m) => parseInt(m[1], 10));
    const distinct = ports.filter((p) => p !== apiPort);
    if (distinct.length > 0) return distinct[0];
  }
  return apiPort + 2000;
}

/**
 * Slugify a git branch into a per-branch database name.
 * e.g. "hotfixes/licence-bug" → "SmartStack_licence_bug".
 * Exported for reuse/tests; the SKILL computes the same default conversationally.
 */
export function deriveDatabaseName(branch: string | null | undefined, base = 'SmartStack'): string {
  if (!branch) return base;
  const tail = branch.replace(/^(feature|feat|hotfix|hotfixes|release|bugfix|fix)\//i, '');
  const slug = tail
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
  return slug.length > 0 ? `${base}_${slug}` : base;
}

// ── Auto-seed connection string (appsettings.Local.json first run) ──────────────

/**
 * Replace the database name in a SQL Server connection string, preserving every other
 * token (server, auth, login/password, pooling, encryption…) VERBATIM. Handles both
 * `Database=` and `Initial Catalog=`, case-insensitively, up to the next `;`. If the
 * string carries neither token, append `Database=<newDb>;`.
 *
 * WHY value-level (not a rebuild): the auto-seed copies the base appsettings connection
 * string and only swaps the per-branch DB — rebuilding via buildSqlConnString would drop
 * unknown options (TrustServerCertificate, MultipleActiveResultSets, custom timeouts…).
 */
export function swapDatabaseName(connStr: string, newDb: string): string {
  const re = /(\b(?:Database|Initial Catalog)\s*=)([^;]*)/i;
  if (re.test(connStr)) return connStr.replace(re, `$1${newDb}`);
  const sep = connStr.length === 0 || connStr.trimEnd().endsWith(';') ? '' : ';';
  return `${connStr}${sep}Database=${newDb}`;
}

/**
 * Build a localhost SQL Server connection string (Windows auth) for `db`, matching
 * templates/project/appsettings.json.template's shape (+ CONN_TAIL). Used as the seed
 * fallback when no base connection string exists to copy.
 */
export function defaultLocalhostConnString(db: string): string {
  return `Server=(local);Database=${db};Integrated Security=true;${CONN_TAIL}`;
}

/**
 * Compute the connection string for an auto-seeded appsettings.Local.json. Pure — the
 * whole per-branch DB policy lives here so it is unit-testable:
 *  - default branch  → keep the base database name (a literal copy of the base config);
 *  - non-default     → isolate per branch via deriveDatabaseName (`SmartStack_<branch>`);
 *  - no base string  → fall back to a localhost Windows-auth default for the target DB.
 */
export function buildSeedConnectionString(input: {
  baseConnString: string | null | undefined;
  baseDatabase: string | null | undefined;
  branch: string | null | undefined;
  isDefaultBranch: boolean;
}): string {
  const baseDb =
    input.baseDatabase && input.baseDatabase !== 'unknown' ? input.baseDatabase : 'SmartStack';
  const targetDb = input.isDefaultBranch ? baseDb : deriveDatabaseName(input.branch, baseDb);
  const base = input.baseConnString ?? '';
  return base.length > 0 ? swapDatabaseName(base, targetDb) : defaultLocalhostConnString(targetDb);
}

// ── Legacy-key port rewrite ────────────────────────────────────────────────────

export interface PortMapping {
  from: number;
  to: number;
}

/**
 * Rewrite every `localhost:<from>` / `127.0.0.1:<from>` occurrence in a single
 * string to its mapped `:<to>`, in ONE pass (so a chain of mappings can never
 * re-rewrite an already-substituted port).
 *
 * WHY: `ss dev configure` only knows the canonical SmartStack keys it writes
 * (`SmartStack.FrontendUrl` / `SmartStack.CorsOrigins`). Real client apps carry
 * the SAME frontend port under OTHER, app-specific keys it cannot enumerate —
 * `Authentication.FrontendUrl`, `Email.BaseUrl`, callback URLs, … A port change
 * that only touched the known keys left those stale (the PickEBike 6173→4100
 * trace: the agent had to hand-edit them, which the SKILL forbids). Rewriting by
 * VALUE (`localhost:<oldPort>`), name-agnostic, makes a port change complete
 * wherever the old port appears — without a hardcoded key list.
 */
export function rewriteLocalhostPort(value: string, mappings: PortMapping[]): string {
  const active = mappings.filter((m) => Number.isInteger(m.from) && Number.isInteger(m.to) && m.from !== m.to);
  if (active.length === 0) return value;
  const map = new Map(active.map((m) => [m.from, m.to]));
  return value.replace(/(localhost|127\.0\.0\.1):(\d{2,5})/g, (full, host: string, p: string) => {
    const to = map.get(parseInt(p, 10));
    return to ? `${host}:${to}` : full;
  });
}

/**
 * Deep-copy a JSON value, applying `rewriteLocalhostPort` to every string leaf.
 * Objects + arrays are recursed; non-strings pass through untouched. Used to
 * sweep an entire appsettings.Local.json so a port change reaches every URL,
 * whatever key holds it.
 */
export function rewritePortsDeep<T>(node: T, mappings: PortMapping[]): T {
  const active = mappings.filter((m) => Number.isInteger(m.from) && Number.isInteger(m.to) && m.from !== m.to);
  if (active.length === 0) return node;
  const walk = (v: unknown): unknown => {
    if (typeof v === 'string') return rewriteLocalhostPort(v, active);
    if (Array.isArray(v)) return v.map(walk);
    if (v !== null && typeof v === 'object') {
      const out: Record<string, unknown> = {};
      for (const [k, val] of Object.entries(v as Record<string, unknown>)) out[k] = walk(val);
      return out;
    }
    return v;
  };
  return walk(node) as T;
}
